import { Badge } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';
import { channelLabel } from '@/data/studio-layouts';
import { mirrorFormatLabel, type MirrorPost } from '@/data/social-mirror';

/* ----------------------------------------------------------------------------
 * PublishMirror — the "What will post" panel. Renders EXACTLY the publish
 * payload from the shared social post model (data/social-mirror.ts, the FE twin
 * of SocialPostModel::fromDraft): the RAW media asset that ships (no baked
 * overlay, no headline/CTA composited onto it) + the caption text as the
 * platform caption. This is the honest counterpart to the styled ContentPreview:
 * ContentPreview shows the *look*, PublishMirror shows the *post*.
 *
 * It reads ONLY a computed MirrorPost — no draft re-interpretation happens here,
 * so it cannot drift from the publisher. Blocked/unsupported posts render an
 * honest "will not publish here" state rather than a fake preview.
 * -------------------------------------------------------------------------- */
export function PublishMirror({ post }: { post: MirrorPost }) {
  const platformLabel = channelLabel(post.platform as never);

  return (
    <div className="rounded-lg border border-border-default bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">What will post</span>
          <Badge tone="neutral">{platformLabel}</Badge>
        </div>
        <span className="text-[11px] text-text-tertiary">Mirrors the publish payload</span>
      </div>

      {post.format === 'blocked' ? (
        <BlockedNotice post={post} />
      ) : (
        <div className="space-y-3">
          {/* The raw media asset that ships — NO scrim, NO baked text overlay. */}
          {post.mediaUrl ? (
            post.mediaKind === 'video' ? (
              <video
                src={post.mediaUrl}
                className="w-full rounded-md border border-border-subtle bg-black object-contain"
                muted
                loop
                playsInline
                autoPlay
                preload="metadata"
                aria-label="Media that will post (raw)"
              />
            ) : (
              <img
                src={post.mediaUrl}
                alt="Media that will post (raw, no overlay)"
                className="w-full rounded-md border border-border-subtle object-contain"
              />
            )
          ) : (
            <div className="rounded-md border border-dashed border-border-subtle bg-inset px-3 py-4 text-center text-xs text-text-tertiary">
              No image or video — this posts as text only.
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge tone="neutral">{mirrorFormatLabel(post.format)}</Badge>
          </div>

          {/* The caption text posted as the platform caption (body_text verbatim). */}
          <div>
            <p className="mb-1 text-[11px] font-medium text-text-secondary">Caption</p>
            {post.caption.trim() !== '' ? (
              <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{post.caption}</p>
            ) : (
              <p className="text-sm italic text-text-tertiary">No caption text.</p>
            )}
          </div>

          {/* FB blog-link as first comment — indicator only (pending Meta App Review). */}
          {post.firstCommentNote ? (
            <div className="rounded-md border border-border-subtle bg-inset p-2">
              <p className="text-[11px] font-medium text-text-secondary">Posts as a first comment</p>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-text-primary">{post.firstCommentNote}</p>
              <p className="mt-1 text-[11px] text-text-tertiary">
                The blog link posts as a first comment (not in the caption). First-comment posting is pending
                Meta App Review.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function BlockedNotice({ post }: { post: MirrorPost }) {
  const label = post.supported ? channelLabel(post.platform as never) : post.platform;
  let message: string;
  if (post.blockedReason === 'ig_requires_media') {
    message = 'Instagram requires an image or video. Add media on the Media step to publish this post.';
  } else if (post.blockedReason === 'unsupported_platform') {
    message = `${label} is not a connected publish target, so this draft will not publish here. Facebook and Instagram are the wired channels.`;
  } else {
    message = 'This draft will not publish in its current state.';
  }
  return (
    <div className={cn('rounded-md border border-warning/30 bg-warning/5 p-3')}>
      <p className="text-sm font-medium text-warning">Will not publish</p>
      <p className="mt-1 text-xs text-text-secondary">{message}</p>
    </div>
  );
}
